import Exon from "./Exon";
import Region from "../../../../../models/Region";
import partition from "../../../../../common/helpers/partition";
import { calcTriploHaplo } from "../../../constants/sequences";

const REFSEQ = "RefSeq";
const ENSEMBL = "Ensembl";

class Transcript {
  constructor(data = {}, chr) {
    const subSeqs = data.subfeatures || [];
    const start = Number(data.start);
    const end = Number(data.end);
    const [nonCoding, coding] = partition(subSeqs, (x) => Exon.isCoding(x));

    this.transcriptName = data.name || "";

    this.geneSymbol = data.gene || "";
    this.strand = data.strand || 1;
    this.region = new Region(start, end, chr);

    this.isCanonical = Boolean(data.canonical);
    this.isMane = Boolean(data.mane_select);
    this.db = this.transcriptName.match(/^ENST/gi) ? ENSEMBL : REFSEQ;
    this.tsl = Number(data.ensembl_support_level) || null;
    this.tslSortVal = this.calcTslSortVal();
    this.totalGeneTranscripts = Number(data.total_gene_transcripts) || 0;

    // haploinsufficiency
    this.haploInsScore = data.haploinsufficiency_score;
    this.haploInsDesc = data.haploinsufficiency_desc;
    this.haploInsPubs = data.haploinsufficiency_pubmed_ids;

    // triplosensitivity
    this.triploSenScore = data.triplosensitivity_score;
    this.triploSenDesc = data.triplosensitivity_desc;
    this.triploSenPubs = data.triplosensitivity_pubmed_ids;

    this.triploHaploIndex = calcTriploHaplo(this);

    this.exons = coding.map((x) => new Exon(x, this));
    this.sequences = nonCoding.map((x) => new Exon(x, this));
    this.codons = [];
  }

  get id() {
    return `${this.geneSymbol}-${this.transcriptName}`;
  }

  get transcript() {
    return this;
  }

  get isRefSeq() {
    return this.db === REFSEQ;
  }

  get isEnsembl() {
    return this.db === ENSEMBL;
  }

  calculateCodons() {
    if (this.codons.length) return this;
    const seqCodons = this.exons.map((seq, i) => seq.fixSplitCodons(i));
    this.codons = [].concat(...seqCodons);
    return this;
  }

  calcTslSortVal() {
    const nonSpecifiedEnsemblTslVal = 0.9;
    return this.tsl || nonSpecifiedEnsemblTslVal;
  }

  unload() {
    this.hidden = true;
    this.isExcluded = true;
    this.codons = [];
    this.sequences = [];
    return this;
  }

  compareTo(other) {
    return [
      Number(other.isMane) - Number(this.isMane), //    [DSC] on mane select ->   -> [...MANE, ...non-MANE]
      Number(other.isCanonical) - Number(this.isCanonical), //  [DSC] on canonical state  -> [...canonicals, ...non-canonicals]
      this.tslSortVal - other.tslSortVal, //                    [ASC] on tsl state        -> [...{{ensembl:TSL === null or REFSEQ}}, ...TSL=1, ...TSL=2, ...TSL=3]
      this.transcriptName.localeCompare(other.transcriptName), //       [ASC] on name             -> [00010, 00011, 00012]
    ].find(Boolean);
  }

  getLink(genome = "hg38") {
    const genomeSubdomain = genome === "hg38" ? "www" : "grch37";
    return this.isEnsembl
      ? `http://${genomeSubdomain}.ensembl.org/Homo_sapiens/Transcript/Summary?t=${this.transcriptName}`
      : `https://www.ncbi.nlm.nih.gov/nuccore/${this.transcriptName}`;
  }
}

export default Transcript;
